export interface FormData {
  // Entry Information
  entry_title_eng: string;
  entry_title_chi: string;
  entry_info: string;

  // Company Information
  company_name_eng: string;
  company_name_chi: string;
  company_info: string;
  company_address: string;
  company_ceo_name: string;
  company_business: string;
  company_url: string;
  company_registration_no: string;
  company_incorporation_no: string;
  company_incorporation_date: string;
  startup: string;
  company_employee_no: string;
  entry_incubation: string;
  entry_incubation_description: string;
  company_logo_1: File | null;
  company_logo_2: File | null;
  corporate_guideline: File | null;

  // Contact Details
  contact_name_1: string;
  contact_title_1: string;
  contact_phone_1: string;
  contact_email_1: string;
  contact_name_2: string;
  contact_title_2: string;
  contact_phone_2: string;
  contact_email_2: string;

  // Award Category
  award_category: string;

  // Judging Materials
  judging_specialfeature_1: string;
  judging_specialfeature_2: string;
  judging_specialfeature_3: string;
  judging_specialfeature_4: string;
  judging_specialfeature_5: string;
  judging_specialfeature_6: string;
  entry_photo: File | null;
  judging_doc_material: File | null;
  judging_video_material_1: string;

  // Declaration
  about_submission_usage: boolean;
  about_application: boolean;
  entry_patent: string;
  entry_patent_number: string;
  entry_patent_title: string;
  entry_patent_proprietor: string;
  entry_patent_country: string;
  entry_patent_date: string;
  entry_patent_status: string;
  entry_dispute: string;
  entry_dispute_description: string;
  event_cost: boolean;
  data_policy_1: boolean;
  data_policy_2: boolean;

  // Captcha
  checknum: string;
}

export interface ValidationError {
  field: string;
  message: string;
}

export class FormValidator {
  private errors: ValidationError[] = [];

  validateForm(data: FormData): ValidationError[] {
    this.errors = [];

    this.validateEntryInformation(data);
    this.validateCompanyInformation(data);
    this.validateContactDetails(data);
    this.validateAwardCategory(data);
    this.validateJudgingMaterials(data);
    this.validateDeclaration(data);
    this.validateCaptcha(data);

    return this.errors;
  }

  // Language validation helper function
  private isEnglishOnly(text: string): boolean {
    // Check if text contains only English letters, numbers, spaces, and common punctuation
    return /^[A-Za-z0-9\s\-_.,!?@#$%^&*()[\]{}:;"'<>/\\|`~+=]+$/.test(text);
  }

  // Check if text contains Chinese characters
  private containsChinese(text: string): boolean {
    return /[\u4e00-\u9fff]/.test(text);
  }

  // Word count validation helper
  private getWordCount(text: string): number {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  }

  private validateEntryInformation(data: FormData) {
    // Entry Title (English) - Must be English only
    if (!data.entry_title_eng.trim()) {
      this.addError('entry_title_eng', 'Entry title in English is required 參賽機構作品名稱（英文）為必填項目');
    } else if (this.containsChinese(data.entry_title_eng)) {
      this.addError('entry_title_eng', 'Please input in English 請使用英文填寫');
    } else if (!this.isEnglishOnly(data.entry_title_eng)) {
      this.addError('entry_title_eng', 'Entry title contains invalid characters 參賽機構作品名稱包含無效字符');
    }

    // Entry Title (Chinese) - Optional but must be Chinese if provided
    if (data.entry_title_chi.trim() && !this.containsChinese(data.entry_title_chi)) {
      this.addError('entry_title_chi', 'Please input in Chinese 請使用中文填寫');
    }

    // Entry Description - Must be English only, 150 words max
    if (!data.entry_info.trim()) {
      this.addError('entry_info', 'Entry description is required 參賽機構作品簡介為必填項目');
    } else if (this.containsChinese(data.entry_info)) {
      this.addError('entry_info', 'Please input in English 請使用英文填寫');
    } else {
      const wordCount = this.getWordCount(data.entry_info);
      if (wordCount < 10) {
        this.addError('entry_info', 'Entry description must be at least 10 words 參賽機構作品簡介至少需要10個單詞');
      } else if (wordCount > 150) {
        this.addError('entry_info', 'Entry description must not exceed 150 words 參賽機構作品簡介不能超過150個單詞');
      }
    }
  }

  private validateCompanyInformation(data: FormData) {
    // Company Name (English) - Must be English only
    if (!data.company_name_eng.trim()) {
      this.addError('company_name_eng', 'Company name in English is required 公司名稱（英文）為必填項目');
    } else if (this.containsChinese(data.company_name_eng)) {
      this.addError('company_name_eng', 'Please input in English 請使用英文填寫');
    }

    // Company Name (Chinese) - Optional but must be Chinese if provided
    if (data.company_name_chi.trim() && !this.containsChinese(data.company_name_chi)) {
      this.addError('company_name_chi', 'Please input in Chinese 請使用中文填寫');
    }

    // Company Description - Must be English only, 150 words max
    if (!data.company_info.trim()) {
      this.addError('company_info', 'Company description is required 公司簡介為必填項目');
    } else if (this.containsChinese(data.company_info)) {
      this.addError('company_info', 'Please input in English 請使用英文填寫');
    } else {
      const wordCount = this.getWordCount(data.company_info);
      if (wordCount < 10) {
        this.addError('company_info', 'Company description must be at least 10 words 公司簡介至少需要10個單詞');
      } else if (wordCount > 150) {
        this.addError('company_info', 'Company description must not exceed 150 words 公司簡介不能超過150個單詞');
      }
    }

    // Company Address - Can be in any language
    if (!data.company_address.trim()) {
      this.addError('company_address', 'Company address is required 公司地址為必填項目');
    }

    // CEO Name - Can be in any language
    if (data.company_ceo_name.trim() && this.containsChinese(data.company_ceo_name) && !this.isEnglishOnly(data.company_ceo_name)) {
      this.addError('company_ceo_name', 'CEO name should be in English or Chinese only 行政總裁姓名應使用英文或中文');
    }

    // Nature of Business - Must be English only
    if (!data.company_business.trim()) {
      this.addError('company_business', 'Nature of business is required 業務性質為必填項目');
    } else if (this.containsChinese(data.company_business)) {
      this.addError('company_business', 'Please input in English 請使用英文填寫');
    }

    // Company URL - Must be valid URL
    if (!data.company_url.trim() || data.company_url === 'http://') {
      this.addError('company_url', 'Company URL is required 公司網址為必填項目');
    } else if (!/^https?:\/\/.+/.test(data.company_url)) {
      this.addError('company_url', 'Please enter a valid URL starting with http:// or https:// 請輸入以http://或https://開頭的有效網址');
    }

    // Business Registration Number - Must be English only
    if (!data.company_registration_no.trim()) {
      this.addError('company_registration_no', 'Business registration certificate number is required 商業登記證號碼為必填項目');
    } else if (this.containsChinese(data.company_registration_no)) {
      this.addError('company_registration_no', 'Please input in English 請使用英文填寫');
    }

    // Incorporation Number - Must be English only
    if (!data.company_incorporation_no.trim()) {
      this.addError('company_incorporation_no', 'Certificate of incorporation number is required 公司註冊證書號碼為必填項目');
    } else if (this.containsChinese(data.company_incorporation_no)) {
      this.addError('company_incorporation_no', 'Please input in English 請使用英文填寫');
    }

    // Incorporation Date - Optional but must be valid date format if provided
    if (data.company_incorporation_date.trim() && !/^\d{4}-\d{2}-\d{2}$/.test(data.company_incorporation_date)) {
      this.addError('company_incorporation_date', 'Please enter a valid date in YYYY-MM-DD format 請以YYYY-MM-DD格式輸入有效日期');
    }

    // Startup Status
    if (!data.startup) {
      this.addError('startup', 'Please indicate if you are a start-up company 請表明是否為初創公司');
    }

    // Employee Count - Optional field
    // Removed required validation as per user request

    // Incubation Programme Status
    if (!data.entry_incubation) {
      this.addError('entry_incubation', 'Please select incubation programme status 請選擇孵化計劃狀態');
    }

    // Incubation Description - Must be English only if provided
    if (data.entry_incubation === 'O' && !data.entry_incubation_description.trim()) {
      this.addError('entry_incubation_description', 'Please specify the incubation programme 請說明孵化計劃');
    } else if (data.entry_incubation_description.trim() && this.containsChinese(data.entry_incubation_description)) {
      this.addError('entry_incubation_description', 'Please input in English 請使用英文填寫');
    }

    // Required File Uploads
    if (!data.company_logo_1) {
      this.addError('company_logo_1', 'AI or EPS logo file is required AI或EPS格式商標檔案為必填項目');
    }

    if (!data.company_logo_2) {
      this.addError('company_logo_2', 'JPEG logo file is required JPEG格式商標檔案為必填項目');
    }

    if (!data.corporate_guideline) {
      this.addError('corporate_guideline', 'Corporate logo and brand guideline file is required 公司商標及品牌指引檔案為必填項目');
    }
  }

  private validateContactDetails(data: FormData) {
    // Primary Contact Name - Can be in any language
    if (!data.contact_name_1.trim()) {
      this.addError('contact_name_1', 'Primary contact name is required 主要聯絡人姓名為必填項目');
    }

    // Primary Contact Title - Must be English only
    if (!data.contact_title_1.trim()) {
      this.addError('contact_title_1', 'Primary contact title is required 主要聯絡人職銜為必填項目');
    } else if (this.containsChinese(data.contact_title_1)) {
      this.addError('contact_title_1', 'Please input in English 請使用英文填寫');
    }

    // Primary Contact Phone - Must be valid phone number
    if (!data.contact_phone_1.trim()) {
      this.addError('contact_phone_1', 'Primary contact phone is required 主要聯絡人電話為必填項目');
    } else if (!/^[0-9]{8,11}$/.test(data.contact_phone_1.replace(/\D/g, ''))) {
      this.addError('contact_phone_1', 'Please enter a valid phone number (8-11 digits) 請輸入有效的電話號碼（8-11位數字）');
    }

    // Primary Contact Email - Must be valid email
    if (!data.contact_email_1.trim()) {
      this.addError('contact_email_1', 'Primary contact email is required 主要聯絡人電郵為必填項目');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contact_email_1)) {
      this.addError('contact_email_1', 'Please enter a valid email address 請輸入有效的電郵地址');
    }

    // Secondary Contact Name - Can be in any language
    if (!data.contact_name_2.trim()) {
      this.addError('contact_name_2', 'Secondary contact name is required 次要聯絡人姓名為必填項目');
    }

    // Secondary Contact Title - Must be English only
    if (!data.contact_title_2.trim()) {
      this.addError('contact_title_2', 'Secondary contact title is required 次要聯絡人職銜為必填項目');
    } else if (this.containsChinese(data.contact_title_2)) {
      this.addError('contact_title_2', 'Please input in English 請使用英文填寫');
    }

    // Secondary Contact Phone - Must be valid phone number
    if (!data.contact_phone_2.trim()) {
      this.addError('contact_phone_2', 'Secondary contact phone is required 次要聯絡人電話為必填項目');
    } else if (!/^[0-9]{8,11}$/.test(data.contact_phone_2.replace(/\D/g, ''))) {
      this.addError('contact_phone_2', 'Please enter a valid phone number (8-11 digits) 請輸入有效的電話號碼（8-11位數字）');
    }

    // Secondary Contact Email - Must be valid email
    if (!data.contact_email_2.trim()) {
      this.addError('contact_email_2', 'Secondary contact email is required 次要聯絡人電郵為必填項目');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contact_email_2)) {
      this.addError('contact_email_2', 'Please enter a valid email address 請輸入有效的電郵地址');
    }
  }

  private validateAwardCategory(data: FormData) {
    if (!data.award_category) {
      this.addError('award_category', 'Please select an award category');
    }
  }

  private validateJudgingMaterials(data: FormData) {
    const requiredFields = [
      { field: 'judging_specialfeature_1', name: 'Innovation & Creativity 創新及創意' },
      { field: 'judging_specialfeature_2', name: 'Functionality & Ergonomics 功能及人體工學' },
      { field: 'judging_specialfeature_3', name: 'Quality 品質' },
      { field: 'judging_specialfeature_4', name: 'Benefits & Impact 效益及影響' },
      { field: 'judging_specialfeature_5', name: 'Market Potential 市場潛力' },
      { field: 'judging_specialfeature_6', name: 'Ecological Compatibility 生態兼容性' }
    ];

    requiredFields.forEach(({ field, name }) => {
      const value = data[field as keyof FormData] as string;
      if (!value || !value.trim()) {
        this.addError(field, `${name} is required ${name}為必填項目`);
      }
    });

    // Video Link - Must be valid URL if provided
    if (data.judging_video_material_1.trim()) {
      if (!/^https?:\/\/.+/.test(data.judging_video_material_1)) {
        this.addError('judging_video_material_1', 'Please enter a valid video URL starting with http:// or https:// 請輸入以http://或https://開頭的有效視頻網址');
      }
    }

    // Check if at least one document is submitted as reference
    if (!data.entry_photo && !data.judging_doc_material) {
      this.addError('supplementary_materials', 'Please submit at least 1 document as reference. 請提交以下最少一項的文件作參考。');
    }
  }

  private validateDeclaration(data: FormData) {
    // Submission Usage Agreement
    if (!data.about_submission_usage) {
      this.addError('about_submission_usage', 'Please agree to the submission usage terms 請同意提交使用條款');
    }

    // Application Declaration
    if (!data.about_application) {
      this.addError('about_application', 'Please confirm the application declaration 請確認申請聲明');
    }

    // Patent Status
    if (!data.entry_patent) {
      this.addError('entry_patent', 'Please select patent status 請選擇專利狀態');
    }

    // Patent Details - Must be English only if patent is declared
    if (data.entry_patent === 'Y') {
      if (!data.entry_patent_number.trim()) {
        this.addError('entry_patent_number', 'Patent number is required when patent is declared 申報專利時專利號碼為必填項目');
      } else if (this.containsChinese(data.entry_patent_number)) {
        this.addError('entry_patent_number', 'Please input in English 請使用英文填寫');
      }

      if (!data.entry_patent_title.trim()) {
        this.addError('entry_patent_title', 'Patent title is required when patent is declared 申報專利時專利標題為必填項目');
      } else if (this.containsChinese(data.entry_patent_title)) {
        this.addError('entry_patent_title', 'Please input in English 請使用英文填寫');
      }

      if (!data.entry_patent_proprietor.trim()) {
        this.addError('entry_patent_proprietor', 'Patent proprietor is required when patent is declared 申報專利時專利擁有人為必填項目');
      } else if (this.containsChinese(data.entry_patent_proprietor)) {
        this.addError('entry_patent_proprietor', 'Please input in English 請使用英文填寫');
      }

      if (!data.entry_patent_country.trim()) {
        this.addError('entry_patent_country', 'Patent country is required when patent is declared 申報專利時專利國家為必填項目');
      } else if (this.containsChinese(data.entry_patent_country)) {
        this.addError('entry_patent_country', 'Please input in English 請使用英文填寫');
      }

      if (!data.entry_patent_date) {
        this.addError('entry_patent_date', 'Patent filing date is required when patent is declared 申報專利時專利申請日期為必填項目');
      } else if (!/^\d{4}-\d{2}-\d{2}$/.test(data.entry_patent_date)) {
        this.addError('entry_patent_date', 'Please enter a valid date in YYYY-MM-DD format 請以YYYY-MM-DD格式輸入有效日期');
      }

      if (!data.entry_patent_status) {
        this.addError('entry_patent_status', 'Patent status is required when patent is declared 申報專利時專利狀態為必填項目');
      }
    }

    // Dispute Status
    if (!data.entry_dispute) {
      this.addError('entry_dispute', 'Please select dispute status 請選擇爭議狀態');
    }

    // Dispute Description - Must be English only if dispute is declared
    if (data.entry_dispute === 'Y' && !data.entry_dispute_description.trim()) {
      this.addError('entry_dispute_description', 'Dispute description is required when dispute is declared 申報爭議時爭議描述為必填項目');
    } else if (data.entry_dispute_description.trim() && this.containsChinese(data.entry_dispute_description)) {
      this.addError('entry_dispute_description', 'Please input in English 請使用英文填寫');
    }

    // Event Administration Cost
    if (!data.event_cost) {
      this.addError('event_cost', 'Please agree to the event administration cost 請同意活動管理費用');
    }

    // Terms and Conditions
    if (!data.data_policy_1) {
      this.addError('data_policy_1', 'Please agree to the Terms and Conditions 請同意條款及細則');
    }

    // Personal Information Collection Statement
    if (!data.data_policy_2) {
      this.addError('data_policy_2', 'Please agree to the Personal Information Collection Statement 請同意個人資料收集聲明');
    }
  }

  private validateCaptcha(data: FormData) {
    // Captcha validation
    if (!data.checknum.trim()) {
      this.addError('checknum', 'Verification code is required 驗證碼為必填項目');
    }
  }

  // Real-time validation for individual fields
  validateField(field: keyof FormData, value: FormData[keyof FormData]): ValidationError | null {

    // Type guard to check if value is a string
    const isStringValue = (val: FormData[keyof FormData]): val is string => {
      return typeof val === 'string';
    };

    // Handle non-string values (boolean, File, null)
    if (!isStringValue(value)) {
      return null; // Non-string fields don't need string validation
    }

    switch (field) {
      case 'entry_title_eng':
        if (!value.trim()) {
          return { field, message: 'Entry title in English is required 參賽機構作品名稱（英文）為必填項目' };
        } else if (this.containsChinese(value)) {
          return { field, message: 'Please input in English 請使用英文填寫' };
        }
        break;

      case 'entry_info':
        if (!value.trim()) {
          return { field, message: 'Entry description is required 參賽機構作品簡介為必填項目' };
        } else if (this.containsChinese(value)) {
          return { field, message: 'Please input in English 請使用英文填寫' };
        } else {
          const wordCount = this.getWordCount(value);
          if (wordCount > 150) {
            return { field, message: 'Entry description must not exceed 150 words 參賽機構作品簡介不能超過150個單詞' };
          }
        }
        break;

      case 'company_name_eng':
        if (!value.trim()) {
          return { field, message: 'Company name in English is required 公司名稱（英文）為必填項目' };
        } else if (this.containsChinese(value)) {
          return { field, message: 'Please input in English 請使用英文填寫' };
        }
        break;

      case 'company_info':
        if (!value.trim()) {
          return { field, message: 'Company description is required 公司簡介為必填項目' };
        } else if (this.containsChinese(value)) {
          return { field, message: 'Please input in English 請使用英文填寫' };
        } else {
          const wordCount = this.getWordCount(value);
          if (wordCount > 150) {
            return { field, message: 'Company description must not exceed 150 words 公司簡介不能超過150個單詞' };
          }
        }
        break;

      case 'contact_email_1':
      case 'contact_email_2':
        if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return { field, message: 'Please enter a valid email address 請輸入有效的電郵地址' };
        }
        break;

      case 'contact_phone_1':
      case 'contact_phone_2':
        if (value.trim() && !/^[0-9]{8,11}$/.test(value.replace(/\D/g, ''))) {
          return { field, message: 'Please enter a valid phone number (8-11 digits) 請輸入有效的電話號碼（8-11位數字）' };
        }
        break;

      case 'company_url':
        if (value.trim() && value !== 'http://' && !/^https?:\/\/.+/.test(value)) {
          return { field, message: 'Please enter a valid URL starting with http:// or https:// 請輸入以http://或https://開頭的有效網址' };
        }
        break;

      case 'judging_video_material_1':
        if (value.trim() && !/^https?:\/\/.+/.test(value)) {
          return { field, message: 'Please enter a valid video URL starting with http:// or https:// 請輸入以http://或https://開頭的有效視頻網址' };
        }
        break;
    }

    return null;
  }

  private addError(field: string, message: string) {
    this.errors.push({ field, message });
  }
}

export const formValidator = new FormValidator();